import { MoreOutlined, SendOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Dropdown,
  DropdownProps,
  Empty,
  MenuProps,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  TabsProps,
  Tag,
  Tooltip
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TerminalApi } from '../../../../apis/terminal.aspi';
import { ROUTE_PATHS } from '../../../../constants/router.constants';
import {
  MerchantDetailModel,
  Terminal,
} from '../../../../models/terminal';
import PaginationComponent from '../../../common/pagination/component/pagination';
import './index.scss';
import SearchComponent from '@/components/common/search-component/search';
import ModalUpdateMerchant from './modal/modalUpdateMerchant';
import ModalUpdateLocation from './modal/modalUpdateLocation';
import ModalUpdateDevice from './modal/modalUpdateDevice';
import showConfirmModal from '@/components/common/modal-confirm';
import showNotification from '@/components/common/notification';
import { LocationApi } from '@/apis/location.api';
import { MerchantApi } from '@/apis/merchant.api';
import LocationContext, { StateData, useLocationContext } from './hook/locationContext';
import useTerminalColumns from './hook/terminalColumns';
import { DeviceFilterType } from '@/models/enums';
import { LocationDetailModel } from '@/models/location';
import { TerminalType } from '@/constants/app.enums';
import { useRecoilValue } from 'recoil';
import { uiSettingState } from '@/states/uiSetting';

function LocationDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [terminals, setTerminals] = useState<Terminal[] | any[]>();
  const [isLoadingTerminal, setLoadingTerminal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filteredItems, setFilteredItems] = useState<MenuProps['items']>([]);
  const [isModalMerchantOpen, setIsModalMerchantOpen] = useState(false);
  const [isModalLocationOpen, setIsModalLocationOpen] = useState(false);
  const [isModalDeviceOpen, setIsModalDeviceOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [isLocationLoading, setLocationLoading] = useState(false);
  const [locationInfo, setLocationInfo] = useState<LocationDetailModel>();
  const [merchantData, setMerchantData] = useState<MerchantDetailModel>();
  const { parentId, id } = useParams();
  const locationData = useContext(LocationContext);
  let props = locationData?.data;
  const isMerchant = props?.locationSelected?.deviceFilterType === DeviceFilterType.Merchant;
  const isDeactivate = isMerchant ? merchantData?.isDeactivate : locationInfo?.isDeactivate;
  const uiSetting = useRecoilValue(uiSettingState);
  const allowEdit = uiSetting.isAllowAddDevice

  useEffect(() => {
    if (props?.locationSelected) {
      fetchDataLocation();
      fetchDataTerminal();
    }
  }, [props?.locationSelected]);

  const fetchDataLocation = async () => {
    await getLocationDetail();
  };

  const fetchDataTerminal = async () => {
    await getTerminalList(pageSize, pageNumber, undefined);
    setSearchText("");
  };

  const getLocationDetail = async () => {
    if (!locationData) return;
    setLocationLoading(true);
    try {
      if (id) {
        if (isMerchant && parentId) {
          const { data } = await MerchantApi.getMerchant(props?.locationSelected?.id ?? "0", props?.locationSelected?.parentId);
          if (data)
            setMerchantData(data);
          setLocationInfo({ id: data.locationId, locationName: data.locationName })
        } else {
          const { data } = await LocationApi.getLocation(props?.locationSelected?.id ?? "0");
          if (data)
            setLocationInfo(data);
        }
      }
    } catch (error) {
      // Handle error
    } finally {
      setLocationLoading(false);
    }
  };

  const getTerminalList = async (
    pageSizeData?: number,
    pageNumberData?: number,
    searchString?: string,
  ) => {
    if (!props?.locationKey) return;
    pageSizeData = pageSizeData || pageSize;
    pageNumberData = pageNumberData || pageNumber;
    searchString = searchString || '';
    setLoadingTerminal(true);
    try {
      const { data } = await TerminalApi.getTerminalListByKey(
        props?.locationKey ?? '0',
        props.locationSelected?.deviceFilterType ?? 1,
        pageNumberData,
        pageSizeData,
        searchString
      );
      setTerminals(data.records);
      setTotalRecords(data.totalRecords);
    } catch (error) { }
    finally {
      setLoadingTerminal(false);
      setSearchText(searchString);
    }
  };

  const TerminalList = () => {

    return (
      <>
        <SearchComponent handleSearch={onSearch} searchText={searchText} className="search-terminal" placeholder="Search by serial number" />
        <Row>
          <Col span={24} style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table
              loading={isLoadingTerminal}
              rowKey={(x) => x.id}
              className={`${props?.isHiddenLocation ? "full" : ""} tb-list-terminal`}
              columns={useTerminalColumns()}
              dataSource={terminals}
              scroll={{ x: 'max-content', y: 450 }}
              tableLayout='auto'
              style={{ width: '100%' }}
              pagination={{
                pageSize: pageSize,
                current: pageNumber,
                style: { display: 'none' },
              }}
            />
          </Col>
          <PaginationComponent
            totalRecords={totalRecords}
            pageSize={pageSize}
            setPageSize={setPageSize}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            handleGetFilter={getTerminalList}
          />
        </Row>
      </>

    );
  };

  const LocationDetail = () => {
    return (
      <Descriptions size="small" layout="vertical" >
        <Descriptions.Item label="Location Name">{locationInfo?.locationName ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Street Number">{locationInfo?.streetNumber ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Zone">{locationInfo?.zone ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Floor Number">{locationInfo?.floorNumber ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Apartment Number">{locationInfo?.apartmentNumber ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Created Time">{locationInfo?.createdOn ? new Date(locationInfo.createdOn).toDateString() : "-"}</Descriptions.Item>
        <Descriptions.Item label="Updated Time">{locationInfo?.modifiedOn ? new Date(locationInfo.modifiedOn).toDateString() : "-"}</Descriptions.Item>
      </Descriptions>
    );
  };

  const MerchantDetail = () => {
    return (
      <Descriptions size="small" layout="vertical" >
        <Descriptions.Item label="Merchant Name">{merchantData?.lszMerchantName ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Email Merchant">{merchantData?.lszEmailAddress ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Phone">{merchantData?.lszPhone ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Mobile Phone">{merchantData?.lszMobilePhone ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Address">{merchantData?.lszStreetAddressGeo ?? "-"}</Descriptions.Item>
        <Descriptions.Item label="Created Time">{merchantData?.createdOn ? new Date(merchantData.createdOn).toDateString() : "-"}</Descriptions.Item>
        <Descriptions.Item label="Updated Time">{merchantData?.modifiedOn ? new Date(merchantData.modifiedOn).toDateString() : "-"}</Descriptions.Item>
      </Descriptions>
    );
  };

  const onSearch = async (value: any) => {
    await getTerminalList(pageSize, pageNumber, value);
  };

  function viewTerminalLinking(id: string) {
    if (id === '') {
      console.error(
        'Error setting state for terminal linking navigation.',
      );
    }
    try {
      locationData?.setData((prevData) => ({
        ...prevData,
        isHiddenLocation: true,
      }));
      navigate(ROUTE_PATHS.TerminalDetail.replace(":id", id));
    } catch (error) {
      console.error(
        'Error setting state for terminal linking navigation.',
        error
      );
    }
  }

  const handleEditLocation = () => {
    setIsModalLocationOpen(true);
    setOpen(false);
  };

  const handleActiveLocation = () => {
    let title = isDeactivate ? "Activate" : "Deactivate";

    const handleConfirm = async () => {
      try {
        let { data: activeResponse } = await LocationApi.activeLocation(locationInfo?.id!, isDeactivate);
        if (activeResponse) {
          showNotification("success", "Update location success.");
          fetchDataLocation();
        }
      } catch (error) { }
    };

    const handleCancel = () => {
      console.log("Cancelled action");
    };

    showConfirmModal({
      type: "warning",
      title: `${title} this location now.`,
      content: t("modal.confirm_text"),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const handleRemoveLocation = () => {
    const handleConfirm = async () => {
      try {
        const { data } = await LocationApi.removeLocation(locationInfo?.id!);
        if (data) {
          showNotification("success", "Remove location success.");
          locationData?.reloadData(true);
        }
      } catch (error) { }
    };

    const handleCancel = () => {
      console.log("Cancelled action");
    };

    showConfirmModal({
      type: "warning",
      title: `Remove this location now.`,
      content: t("modal.confirm_text"),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const handleActivateMerchant = () => {
    let title = isDeactivate ? "Activate" : "Deactivate";

    const handleConfirm = async () => {
      try {
        let { data: activeResponse } = await MerchantApi.activateMerchant(
          merchantData?.id!, isDeactivate
        );
        if (activeResponse) {
          showNotification("success", "Update merchant success.");
          fetchDataLocation();
        }
      } catch (error) { }
    };

    const handleCancel = () => {
      console.log("Cancelled action");
    };

    showConfirmModal({
      type: "warning",
      title: `${title} this merchant now.`,
      content: t("modal.confirm_text"),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const handleEditMerchant = () => {
    setIsModalMerchantOpen(true);
    setOpen(false);
  };

  const handleRemoveMerchant = () => {
    const handleConfirm = async () => {
      try {
        const { data } = await MerchantApi.removeMerchant(merchantData?.id!);
        if (data) {
          showNotification("success", "Remove merchant success.");
          locationData?.reloadData(true);
        }
      } catch (error) { }
    };

    const handleCancel = () => {
      console.log("Cancelled action");
    };

    showConfirmModal({
      type: "warning",
      title: `Remove this merchant now.`,
      content: t("modal.confirm_text"),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const handleEditDevice = () => {
    setIsModalDeviceOpen(true);
    setOpen(false);
  };

  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {

      if (isMerchant) {
        setFilteredItems(merchantMenu);
      } else {
        setFilteredItems(locationMenu);
      }
      setOpen(nextOpen);
    }
  };

  const handleCancelMerchantModal = (isUpdated?: boolean) => {
    setIsModalMerchantOpen(false);
    if (isUpdated) {
      fetchDataLocation();
    }
  };

  const handleCancelLocationModal = (isUpdated?: boolean) => {
    setIsModalLocationOpen(false);
    if (isUpdated) {
      fetchDataLocation();
    }
  };

  const handleCancelDeviceModal = (isUpdated?: boolean) => {
    setIsModalDeviceOpen(false);
    if (isUpdated) {
      fetchDataTerminal();
      locationData?.reloadData();
    }
  };

  const locationMenu: MenuProps["items"] = [
    {
      key: "1",
      label: "Edit Location",
      onClick: handleEditLocation,
    },
    {
      key: "2",
      label: isDeactivate ? "Activate Location" : "Deactivate Location",
      onClick: handleActiveLocation,
    },
    {
      key: "3",
      label: "Add Device",
      onClick: handleEditDevice,
    },
    ...(isDeactivate
      ? [
        {
          key: "4",
          label: "Remove",
          onClick: handleRemoveLocation,
        },
      ]
      : []),
  ];

  const merchantMenu: MenuProps["items"] = [
    {
      key: "1",
      label: "Edit Merchant",
      onClick: handleEditMerchant,
    },
    {
      key: "2",
      label: isDeactivate ? "Activate Merchant" : "Deactivate Merchant",
      onClick: handleActivateMerchant,
    },
    {
      key: "3",
      label: "Add Device",
      onClick: handleEditDevice,
    },
    ...(isDeactivate
      ? [
        {
          key: "4",
          label: "Remove",
          onClick: handleRemoveMerchant,
        },
      ]
      : []),
  ];

  const items: TabsProps['items'] = [
    {
      key: 'detail-tab',
      label: <Row className="h6-semiBold">{isMerchant ? "Merchant Detail" : "Location Detail"}</Row>,
      children: (
        <div style={{ maxHeight: 'calc(100vh - 390px)', overflowX: "auto" }}>
          <Card
            style={{ marginTop: 5 }}
            type="inner"
            title={isMerchant ? "Merchant Information" : "Location Information"}
          >
            {isMerchant ? <MerchantDetail /> : <LocationDetail />}
          </Card>
          <Card
            style={{ marginTop: 16 }}
            type="inner"
            title="Device Statistics"
          >
            <Empty />
          </Card>
        </div>
      ),
    },
    {
      key: 'terminal-list-tab',
      label: <Row className="h6-semiBold">Devices List</Row>,
      children:<TerminalList />
    },
  ];

  const useTerminalColumns = (): ColumnsType<Terminal> => {
    const { t } = useTranslation();

    const handleRemoveTerminal = (terminalId: string) => {
      const handleConfirm = async () => {
        try {
          const { data } = await TerminalApi.removeDevice(terminalId);
          if (data) {
            showNotification("success", "Remove device success.");
            fetchDataTerminal();
          }
        } catch (error) { }
      };

      const handleCancel = () => {
        console.log("Cancelled action");
      };

      showConfirmModal({
        type: "warning",
        title: `Remove this device now.`,
        content: t("modal.confirm_text"),
        onOk: handleConfirm,
        onCancel: handleCancel,
      });
    };

    const handleActiveDevice = (terminalId: string, isDeactivate: boolean) => {
      let title = isDeactivate ? "Activate" : "Deactivate";

      const handleConfirm = async () => {
        try {
          let { data: activeResponse } = await TerminalApi.activateDevice(terminalId, isDeactivate);
          if (activeResponse) {
            showNotification("success", `${title} device success.`);
            fetchDataTerminal();
          }
        } catch (error) { }
      };

      const handleCancel = () => {
        console.log("Cancelled action");
      };

      showConfirmModal({
        type: "warning",
        title: `${title} this device now.`,
        content: t("modal.confirm_text"),
        onOk: handleConfirm,
        onCancel: handleCancel,
      });
    };

    return [
      {
        title: t('device.title.terminalName'),
        key: '1',
        ellipsis: true,
        render: (text: string, record: Terminal) => (
          <Tooltip title={record.lszTerminalName}>
            {record.lszTerminalName}
          </Tooltip>
        ),
      },
      {
        title: t('device.title.terminalSerialNumber'),
        key: '2',
        ellipsis: true,
        render: (text: string, record: Terminal) => (
          <Tooltip title={record.terminalSerialNumber}>
            {record.terminalSerialNumber}
          </Tooltip>
        ),
      },
      {
        title: t('device.title.type'),
        key: '3',
        ellipsis: true,
        render: (text: string, record: Terminal) => {
          const terminalType = record.terminalType === TerminalType.Kiosk ? "Kiosk" : "Payment";
          const tooltipTitle = terminalType; // Tooltip title can be the same as terminal type

          return (
            <Tooltip title={tooltipTitle}>
              {terminalType}
            </Tooltip>
          );
        },
      },
      {
        key: 4,
        title: "Status",
        className: "col-status",
        dataIndex: "isDeactivate",
        render: (isDeactivate) => {
          return (
            <>
              {isDeactivate && (
                <Badge
                  status="error"
                  text={t("sysConfig.button.deactive")}
                />
              )}
              {!isDeactivate && (
                <Badge
                  status="success"
                  text={t("sysConfig.button.active")}
                />
              )}
            </>
          );
        },
      },
      {
        title: t('device.title.model'),
        key: '5',
        ellipsis: true,
        render: (text: string, record: Terminal) => (
          <Tooltip title={record.model}>
            {record.model}
          </Tooltip>
        ),
      },
      {
        key: "6",
        className: "col-action",
        render: (_, record) => (
          <Space
            size={[6, 12]}
            wrap
            className="terminal-columns">
            {allowEdit && <Tooltip title={record.isDeactivate ? "Activate" : "Deactive"}>
              <Button
                className={
                  record.isDeactivate
                    ? "action-grp-deactive-btn"
                    : "action-grp-active-btn"
                }
                icon={
                  <i
                    className={
                      record.isDeactivate ? "icon-ok-1" : "icon-cancel-1"
                    }
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  handleActiveDevice(record.id!.toString(), record.isDeactivate!);
                }
                }>
                {record.isDeactivate ? (
                  <span className="active">{t("sysConfig.button.active")}</span>
                ) : (
                  <span className="deactive">{t("sysConfig.button.deactive")}</span>
                )}
              </Button>
            </Tooltip>}
            {allowEdit && <Tooltip title="Delete">
              <Button
                className="action-grp-delete-btn"
                icon={
                  <i
                    className="icon-delete"
                  />
                }
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemoveTerminal(record.id!.toString());
                }}
              />
            </Tooltip>
            }
            <Tooltip title="Go to detail">
              <Button onClick={(event) => {
                event.stopPropagation();
                viewTerminalLinking(record.id!.toString());
              }}
                className="action-grp-edit-btn"
                icon={<SendOutlined />}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];
  };

  return (
    <>
      <Spin spinning={isLocationLoading} size='large'>
        <Card className="device-info-card">
          <Space className="card-body" direction="vertical" style={{ width: '100%' }}>
            <div className="header">
              <span className="device-id h5-semiBold">{isMerchant ? merchantData?.lszMerchantName : locationInfo?.locationName}</span>
              <Space size="middle">
                {allowEdit && <Dropdown
                  className="icon-detail"
                  menu={{
                    items: filteredItems,
                  }}
                  placement="bottomRight"
                  trigger={["click"]}
                  open={open}
                  onOpenChange={handleOpenChange}>
                  <MoreOutlined />
                </Dropdown>}
                <Tag className='icon-status' color={isDeactivate ? "error" : "success"}>
                  {isDeactivate ? "Deactivate" : "Activate"}
                </Tag>
              </Space>
            </div>
            <span className="info-text">
            </span>
          </Space>
        </Card>

        <Tabs
          items={items}
          className="terminal-title"
        />

      </Spin>

      <ModalUpdateMerchant
        visible={isModalMerchantOpen}
        onClose={handleCancelMerchantModal}
        currentId={merchantData?.id}
        locationId={merchantData?.locationId}
        reloadLocationData={locationData?.reloadData}
      />

      <ModalUpdateLocation
        visible={isModalLocationOpen}
        onClose={handleCancelLocationModal}
        currentId={locationInfo?.id}
        reloadLocationData={locationData?.reloadData}
      />

      <ModalUpdateDevice
        visible={isModalDeviceOpen}
        onClose={handleCancelDeviceModal}
        isMerchant={isMerchant}
        merchantData={merchantData}
        locationData={locationInfo}
      />

    </>
  );
}
export default LocationDetail;
